import React, { useEffect, useRef, useState, useCallback } from 'react';

// Global animation manager to batch all animations
class AnimationManager {
  constructor() {
    this.animations = new Map();
    this.isRunning = false;
    this.rafId = null;
  }

  addAnimation(id, startValue, endValue, duration, onUpdate, onComplete) {
    const startTime = performance.now();
    const animation = {
      id,
      startValue,
      endValue,
      duration,
      startTime,
      onUpdate,
      onComplete
    };
    
    this.animations.set(id, animation);
    
    if (!this.isRunning) {
      this.startLoop();
    }
  }

  removeAnimation(id) {
    this.animations.delete(id);
    if (this.animations.size === 0) {
      this.stopLoop();
    }
  }

  startLoop() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.rafId = requestAnimationFrame(this.loop.bind(this));
  }

  stopLoop() {
    if (!this.isRunning) return;
    this.isRunning = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  loop(now) {
    if (!this.isRunning) return;

    const completedAnimations = [];
    
    for (const [id, animation] of this.animations) {
      const elapsed = now - animation.startTime;
      const progress = Math.min(elapsed / animation.duration, 1);
      
      // Use easing function for smoother animation
      const easedProgress = this.easeOutCubic(progress);
      const currentValue = Math.round(
        animation.startValue + (animation.endValue - animation.startValue) * easedProgress
      );
      
      animation.onUpdate(currentValue);
      
      if (progress >= 1) {
        completedAnimations.push(id);
        if (animation.onComplete) {
          animation.onComplete();
        }
      }
    }
    
    // Remove completed animations
    completedAnimations.forEach(id => this.animations.delete(id));
    
    if (this.animations.size === 0) {
      this.stopLoop();
    } else {
      this.rafId = requestAnimationFrame(this.loop.bind(this));
    }
  }

  // Smooth easing function
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }
}

// Global instance
const animationManager = new AnimationManager();

const AnimatedNumber = React.memo(({ value, duration = 500, className = '' }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const animationId = useRef(null);
  const prevValue = useRef(value);

  const updateValue = useCallback((newValue) => {
    setDisplayValue(newValue);
  }, []);

  useEffect(() => {
    if (prevValue.current === value) return;
    
    const startValue = prevValue.current;
    const endValue = value;
    
    // Remove previous animation if it exists
    if (animationId.current) {
      animationManager.removeAnimation(animationId.current);
    }
    
    // Generate unique animation ID
    animationId.current = `animated-${Math.random().toString(36).substr(2, 9)}`;
    
    // Add to animation manager
    animationManager.addAnimation(
      animationId.current,
      startValue,
      endValue,
      duration,
      updateValue,
      () => {
        prevValue.current = value;
      }
    );
    
    return () => {
      if (animationId.current) {
        animationManager.removeAnimation(animationId.current);
      }
    };
  }, [value, duration, updateValue]);

  return <span className={className}>{displayValue}</span>;
});

export default AnimatedNumber; 